/**
 * Changing the form itself: its heading, its button, and what happens after.
 *
 * The same endpoint a rename goes through, and it carries the same trap: a
 * `PATCH` on a view answers with the view serialised *without* its filters and
 * sortings, and so does the `view_updated` broadcast. Writing the answer back
 * would set a submit message and quietly unfilter the view -- so the change is
 * applied field by field and the answer is thrown away.
 *
 * **Applied before the request.** None of these decides which rows exist, so
 * there is nothing to refetch and nothing to be wrong about while waiting; and
 * the heading somebody just typed snapping back for a round trip reads as the
 * form having refused it. The acting tab is excluded from its own broadcast,
 * so nothing is coming over the socket to do this instead.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View } from '@/api/types'
import { patchView } from '../table/view-cache'

/** What a form's owner can say about the form as a whole. */
export interface FormChanges {
  title?: string
  intro?: string
  submit_text?: string
  submit_action?: 'MESSAGE' | 'REDIRECT'
  submit_action_message?: string
  submit_action_redirect_url?: string
}

export function useUpdateForm(
  tableId: number,
  viewId: number,
): (changes: FormChanges) => void {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const update = useMutation({
    mutationFn: (changes: FormChanges): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the seven view types and the compiler cannot know which one a
          // view id names.
          body: changes as never,
        }),
      ),

    onMutate: (changes) => {
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined
          ? views
          : // No guard on `view.type`. The id this hook was built with is a
            // form's, so a check for one would be a claim the code cannot
            // fail -- and a guard no test can tell from its absence is a
            // guard that is not there.
            patchView(views, viewId, (view) => ({ ...view, ...changes })),
      )
      return { previous }
    },

    onError: (_error, _changes, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  return (changes) => {
    update.mutate(changes)
  }
}
